/**
 * Public landing page for a mailed roof estimate (/m/:token).
 *
 * This is where a homeowner arrives after scanning the QR code printed on the
 * piece we mailed them, so it renders outside the app shell: no sidebar, no
 * navigation, and never a login prompt. Everything shown here comes from the
 * unauthenticated endpoint, which deliberately exposes no internal ids.
 */

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Card, Skeleton, Stack, Typography } from '@mui/joy';
import { Warning as WarningIcon } from '@phosphor-icons/react';
import { apiErrorStatus, publicMailerApi } from '../../lib/directMailApi';
import type {
  PublicMailerCampaign,
  PublicMailerClaimRequest,
  PublicMailerQuote,
  PublicMailerQuoteDetail,
} from '../../types/directMail';
import { MailerBrandHeader } from '../../components/directmail/public/MailerBrandHeader';
import { MailerQuoteHero } from '../../components/directmail/public/MailerQuoteHero';
import { MailerClaimForm } from '../../components/directmail/public/MailerClaimForm';
import { MailerClaimConfirmation } from '../../components/directmail/public/MailerClaimConfirmation';
import { brandPalette, formatPhoneNumber } from '../../components/directmail/public/brand';

/**
 * A pin with no campaign or no frozen quote should still land the homeowner on
 * a page with a working contact form rather than a blank screen.
 */
const FALLBACK_CAMPAIGN: PublicMailerCampaign = {
  headline: '',
  offer_text: '',
  disclaimer_text: '',
  show_financing: false,
};

const FALLBACK_QUOTE_DETAIL: PublicMailerQuoteDetail = {
  address: '',
  roof_area_sqft: null,
  squares: null,
  predominant_pitch: '',
  price_low: null,
  price_high: null,
};

/**
 * A homeowner gets copy, not an error body. The public endpoints answer with
 * DRF's own wording — "Request was throttled. Expected available in 3600
 * seconds.", field-keyed validation dicts — which has no place on a landing
 * page, so the status alone chooses what we say.
 */
function quoteLoadMessage(error: unknown): string {
  switch (apiErrorStatus(error)) {
    case 404:
      return "That estimate code isn't active anymore.";
    case 429:
      return 'This page has been opened a lot in the last hour. Please try again shortly.';
    default:
      return "We couldn't load your estimate right now. Please try again in a moment.";
  }
}

function claimErrorMessage(error: unknown): string {
  switch (apiErrorStatus(error)) {
    case 400:
      return 'Please double-check your name and email, then try again.';
    case 404:
      return "This estimate link is no longer active — give us a call and we'll help.";
    case 429:
      return "That's a few too many tries. Please give us a call instead.";
    default:
      return "We couldn't send that just now. Please try again, or give us a call.";
  }
}

function LoadingState() {
  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2.5, sm: 4 } }}>
      <Stack spacing={2}>
        <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 'md' }} />
        <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 'md' }} />
      </Stack>
    </Box>
  );
}

function UnavailableState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Card variant="outlined" sx={{ maxWidth: 460, textAlign: 'center', p: 3 }}>
        <Stack spacing={1.5} alignItems="center">
          <Box sx={{ color: 'neutral.400', display: 'flex' }}>
            <WarningIcon size={52} />
          </Box>
          <Typography level="h4">This estimate isn't available</Typography>
          <Typography level="body-md" textColor="text.secondary">
            {message}
          </Typography>
          <Typography level="body-sm" textColor="text.tertiary">
            Double-check the link printed on your mailer, or call the number on it and we'll pull
            your estimate up for you.
          </Typography>
          {onRetry && (
            <Button size="lg" variant="soft" onClick={onRetry} sx={{ minHeight: 48, mt: 1 }}>
              Try again
            </Button>
          )}
        </Stack>
      </Card>
    </Box>
  );
}

export function MailerQuotePage() {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<PublicMailerQuote | null>(null);
  const [loadError, setLoadError] = useState<string | null>(
    token ? null : 'That link is missing its estimate code.',
  );
  const [loading, setLoading] = useState(Boolean(token));
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [claimOutcome, setClaimOutcome] = useState<'none' | 'new' | 'existing'>('none');
  // A dead token is final; a network blip or a throttle is worth another try,
  // and a homeowner who gives up here is a lead the mailer already paid for.
  const [loadRetryable, setLoadRetryable] = useState(false);

  // The GET records a scan, so it must fire exactly once per token — StrictMode
  // remounts the page in development and would otherwise double-count it.
  const requestedToken = useRef<string | null>(null);

  useEffect(() => {
    if (!token || requestedToken.current === token) return;
    requestedToken.current = token;

    publicMailerApi
      .getQuote(token)
      .then((data) => {
        setQuote(data);
        setLoadError(null);
      })
      .catch((error) => {
        setLoadError(quoteLoadMessage(error));
        setLoadRetryable(apiErrorStatus(error) !== 404);
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!quote) return;
    const previousTitle = document.title;
    document.title = `Your roof estimate · ${quote.organization.name}`;
    return () => {
      document.title = previousTitle;
    };
  }, [quote]);

  const handleClaim = (request: PublicMailerClaimRequest) => {
    if (!token) return;
    setSubmitting(true);
    setSubmitError(null);
    publicMailerApi
      .claim(token, request)
      .then((response) => {
        // A second submit — a double tap, a back button, the other homeowner
        // in the house — answers 200 with `already_claimed` rather than an
        // error, so it reads as a thank-you and only the wording changes.
        const onFile = (response as { already_claimed?: boolean }).already_claimed === true;
        setClaimOutcome(onFile ? 'existing' : 'new');
      })
      .catch((error) => {
        // A 409 is not what the endpoint answers today, but treating "someone
        // already claimed it" as a failure would be the wrong outcome if it did.
        if (apiErrorStatus(error) === 409) {
          setClaimOutcome('existing');
          return;
        }
        setSubmitError(claimErrorMessage(error));
      })
      .finally(() => setSubmitting(false));
  };

  if (loading) return <LoadingState />;
  if (loadError || !quote) {
    return (
      <UnavailableState
        message={loadError ?? "We couldn't find that estimate."}
        onRetry={loadRetryable ? () => window.location.reload() : undefined}
      />
    );
  }

  const organization = quote.organization;
  const campaign = quote.campaign ?? FALLBACK_CAMPAIGN;
  const detail = quote.quote ?? FALLBACK_QUOTE_DETAIL;
  const palette = brandPalette(organization.brand_color);
  const showConfirmation = claimOutcome !== 'none' || quote.already_claimed;
  const alreadyOnFile =
    claimOutcome === 'existing' || (claimOutcome === 'none' && quote.already_claimed);

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.level1' }}>
      <MailerBrandHeader organization={organization} palette={palette} />

      <Box
        sx={{
          maxWidth: 720,
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: { xs: 2.5, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 2, sm: 3 },
        }}
      >
        <MailerQuoteHero campaign={campaign} quote={detail} palette={palette} />

        {showConfirmation ? (
          <MailerClaimConfirmation
            organization={organization}
            palette={palette}
            alreadyOnFile={alreadyOnFile}
          />
        ) : (
          <MailerClaimForm
            palette={palette}
            submitting={submitting}
            error={submitError}
            onSubmit={handleClaim}
          />
        )}

        {campaign.disclaimer_text && (
          <Typography
            level="body-xs"
            textColor="text.tertiary"
            sx={{ textAlign: 'center', whiteSpace: 'pre-line' }}
          >
            {campaign.disclaimer_text}
          </Typography>
        )}

        <Stack spacing={0.5} alignItems="center" sx={{ pt: 1, pb: 3 }}>
          <Typography level="title-sm" sx={{ fontWeight: 700 }}>
            {organization.name}
          </Typography>
          {organization.phone && (
            <Typography level="body-sm" textColor="text.secondary">
              {formatPhoneNumber(organization.phone)}
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
